/**
 * Monochrome crop marks for the GISTDA pulse panel.
 *
 * These replace four full-colour emoji (🌾 🥔 🌽 🎋) and a 🔥. Emoji in an
 * operations console is not just decoration: a pictograph at 15px reads as a
 * sticker, carries no brand colour, and renders differently on every
 * platform. These are 16×16 single-stroke marks that inherit `currentColor`,
 * so they sit in the house palette and change with the theme like everything
 * else.
 *
 * The shapes are deliberately abstract. At this size a detailed illustration
 * turns to mud; a silhouette is legible at 12px and honest about what it is.
 */
import type { ReactElement } from "react";

export type CropMark = "rice" | "cassava" | "maize" | "sugarcane";

const MARKS: Record<CropMark, ReactElement> = {
  // Rice — a stalk bending under a head of grains.
  rice: (
    <>
      <path d="M3 15c3-1 5-4 6-8" />
      <path d="M9 7c1.5-1.5 3-1 2.5 1S9.5 9.5 9 7z" />
      <path d="M7.5 10.5c1.5-1.4 3-.8 2.4.4S8 12 7.5 10.5z" />
      <path d="M6 13c1.3-1.2 2.6-.6 2 .5S6.6 14.4 6 13z" />
    </>
  ),
  // Cassava — a tuber with a pair of leaves.
  cassava: (
    <>
      <path d="M7 7c2.5 0 4 2 4 4.5S9.5 15 7 15 3 13 3 11.5 4.5 7 7 7z" />
      <path d="M7 7V3" />
      <path d="M7 4.5 4.5 2.5M7 4.5 9.5 2.5" />
    </>
  ),
  // Maize — a cob with kernel lines.
  maize: (
    <>
      <ellipse cx="8" cy="9" rx="3" ry="5.5" />
      <path d="M5.6 6.6 10.4 8.2M5.3 9.2h5.4M5.6 11.8 10.4 10.2" />
      <path d="M8 3.5V1.5" />
    </>
  ),
  // Sugarcane — two segmented stalks.
  sugarcane: (
    <>
      <path d="M6 15V3M10 15V3" />
      <path d="M4.5 6.5h3M4.5 10h3M8.5 8h3M8.5 11.5h3" />
      <path d="M6 4 3.5 2M10 4l2.5-2" />
    </>
  ),
};

export function CropIcon({ mark }: { mark: CropMark }) {
  return (
    <svg
      className="gistda-crop-icon"
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {MARKS[mark]}
    </svg>
  );
}

/** A single-stroke flame for the fire-hotspot rows. */
export function FireMark() {
  return (
    <svg
      className="gistda-fire-dot"
      viewBox="0 0 16 16"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 14.5c2.2 0 3.8-1.5 3.8-3.6 0-2.7-2.3-3.4-2-6.4-1.6.9-2.6 2.3-2.6 3.8 0 .8-.6 1.2-1.1.8-.4-.3-.5-.9-.5-1.4-1 1.1-1.4 2.3-1.4 3.4 0 2 1.6 3.4 3.8 3.4z" />
    </svg>
  );
}